import type { OddballParser } from '../parser.js';

export interface WebMidiAdapterOptions {
  /** Device name filter (default: matches "ODD") */
  deviceNameFilter?: string;
  /** Called when connection status changes */
  onStatusChange?: (message: string, state: 'connected' | 'disconnected' | 'error') => void;
}

// Web MIDI API types — the built-in DOM types are incomplete in many TS versions,
// so we define minimal interfaces here for portability.
interface MIDIPort {
  name: string | null;
  id: string;
  state: string;
  type: string;
  onmidimessage: ((e: { data: Uint8Array }) => void) | null;
}

interface MIDIAccessLike {
  inputs: Map<string, MIDIPort>;
  onstatechange: ((e: unknown) => void) | null;
}

export class WebMidiAdapter {
  private parser: OddballParser;
  private options: WebMidiAdapterOptions;
  private midiAccess: MIDIAccessLike | null = null;
  private input: MIDIPort | null = null;

  constructor(parser: OddballParser, options: WebMidiAdapterOptions = {}) {
    this.parser = parser;
    this.options = options;
  }

  async connect(): Promise<boolean> {
    if (typeof navigator === 'undefined' || !navigator.requestMIDIAccess) {
      this.notify('Web MIDI not supported', 'error');
      return false;
    }

    try {
      this.midiAccess = await navigator.requestMIDIAccess({ sysex: false }) as unknown as MIDIAccessLike;
      this.midiAccess.onstatechange = () => this.findInput();
      return this.findInput();
    } catch (err) {
      this.notify(`MIDI access denied: ${(err as Error).message}`, 'error');
      return false;
    }
  }

  disconnect(): void {
    if (this.input) {
      this.input.onmidimessage = null;
      this.input = null;
      this.notify('Disconnected', 'disconnected');
    }
  }

  private findInput(): boolean {
    if (!this.midiAccess) return false;

    const filter = (this.options.deviceNameFilter ?? 'ODD').toUpperCase();
    const inputs = Array.from(this.midiAccess.inputs.values());
    const match = inputs.find(i => i.name?.toUpperCase().includes(filter));
    const target = match ?? inputs[0] ?? null;

    if (target && target !== this.input) {
      if (this.input) this.input.onmidimessage = null;
      this.input = target;
      this.input.onmidimessage = (e) => {
        if (e.data) this.parser.feed(e.data);
      };
      this.notify(`Connected: ${target.name}`, 'connected');
      return true;
    }

    if (!target) {
      this.notify('No MIDI device found', 'error');
      return false;
    }

    return !!this.input;
  }

  private notify(message: string, state: 'connected' | 'disconnected' | 'error'): void {
    this.options.onStatusChange?.(message, state);
  }
}
